import React from 'react';
import { Zap, Users, Building2, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { CreateWorkflowRequest } from '@/types/workflow';

interface Template {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  stages: number;
  approvers: number;
  useCase: string;
  workflow: CreateWorkflowRequest;
}

const TEMPLATES: Template[] = [
  {
    id: 'single-manager',
    name: 'Single Manager Approval',
    description: 'Simple one-stage approval by direct manager',
    icon: <Users className="h-8 w-8 text-blue-500" />,
    stages: 1,
    approvers: 1,
    useCase: 'Best for small teams with straightforward approval needs',
    workflow: {
      name: 'Single Manager Approval',
      description: 'Simple one-stage approval by direct manager',
      isActive: true,
      isDefault: false,
      stages: [
        {
          name: 'Manager Review',
          description: 'Direct manager reviews and approves timesheet',
          stageOrder: 1,
          approvalType: 'any',
          requiredApprovals: 1,
          approvers: [
            {
              approverType: 'manager',
              approverOrder: 1,
            },
          ],
        },
      ],
    },
  },
  {
    id: 'two-stage',
    name: 'Two-Stage Approval',
    description: 'Manager review followed by department head approval',
    icon: <Building2 className="h-8 w-8 text-purple-500" />,
    stages: 2,
    approvers: 2,
    useCase: 'Ideal for medium-sized organizations with departmental structure',
    workflow: {
      name: 'Two-Stage Approval',
      description: 'Manager review followed by department head approval',
      isActive: true,
      isDefault: false,
      stages: [
        {
          name: 'Manager Review',
          description: 'Direct manager reviews timesheet',
          stageOrder: 1,
          approvalType: 'any',
          requiredApprovals: 1,
          approvers: [
            {
              approverType: 'manager',
              approverOrder: 1,
            },
          ],
        },
        {
          name: 'Department Head Approval',
          description: 'Department head provides final approval',
          stageOrder: 2,
          approvalType: 'any',
          requiredApprovals: 1,
          approvers: [
            {
              approverType: 'department_head',
              approverOrder: 1,
            },
          ],
        },
      ],
    },
  },
  {
    id: 'no-approval',
    name: 'Auto-Approve',
    description: 'No approval required - timesheets auto-approve',
    icon: <CheckCircle2 className="h-8 w-8 text-green-500" />,
    stages: 0,
    approvers: 0,
    useCase: 'Perfect for trusted teams or contractors with flexible time tracking',
    workflow: {
      name: 'Auto-Approve',
      description: 'Timesheets are automatically approved without review',
      isActive: true,
      isDefault: false,
      stages: [],
    },
  },
];

interface QuickStartTemplatesProps {
  onSelectTemplate: (workflow: CreateWorkflowRequest) => void;
  onSkip: () => void;
}

export function QuickStartTemplates({ onSelectTemplate, onSkip }: QuickStartTemplatesProps) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center mb-4">
          <div className="bg-primary/10 p-3 rounded-full">
            <Zap className="h-8 w-8 text-primary" />
          </div>
        </div>
        <h2 className="text-3xl font-bold">Quick Start Templates</h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Choose a pre-configured template to get started quickly, or skip to create a custom workflow from scratch
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-8">
        {TEMPLATES.map((template) => (
          <Card
            key={template.id}
            className="relative hover:shadow-lg transition-shadow cursor-pointer group"
          >
            <CardHeader className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="p-3 bg-muted rounded-lg group-hover:scale-110 transition-transform">
                  {template.icon}
                </div>
                <div className="flex gap-2">
                  <Badge variant="secondary" className="text-xs">
                    {template.stages} {template.stages === 1 ? 'Stage' : 'Stages'}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {template.approvers} {template.approvers === 1 ? 'Approver' : 'Approvers'}
                  </Badge>
                </div>
              </div>
              <div>
                <CardTitle className="text-xl mb-2">{template.name}</CardTitle>
                <CardDescription className="text-sm">
                  {template.description}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted/50 p-3 rounded-md">
                <p className="text-sm text-muted-foreground italic">
                  {template.useCase}
                </p>
              </div>
              
              {template.stages > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase">
                    Workflow Steps:
                  </p>
                  <div className="space-y-1">
                    {template.workflow.stages.map((stage, index) => (
                      <div key={index} className="flex items-start gap-2 text-sm">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <span className="text-muted-foreground">{stage.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={() => onSelectTemplate(template.workflow)}
                className="w-full"
                variant="default"
              >
                Use This Template
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="text-center pt-4">
        <Button onClick={onSkip} variant="ghost" size="lg">
          Skip - Create Custom Workflow
        </Button>
      </div>
    </div>
  );
}
